/**
 * Avatar Rendering Engine Module
 * Implements: AvatarLoader, AvatarCustomizer, AnimationController, RenderEngine, EventSyncAdapter, ExtensibilityHooks
 * Based on specs/modules/avatar_rendering_engine.pseudo
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';


///////////////////////
// Utility & Caching //
///////////////////////

const avatarCache = new Map();
const supportedParsers = {};
const animationLibrary = {};

///////////////////////
// AvatarLoader
///////////////////////

class AvatarLoader {
  static async loadAvatar(avatarId, options) {
    const { format, sourceUrl, useCache = true } = options;
    if (!avatarId) {
      throw new Error('InvalidAvatarId');
    }
    if (useCache && avatarCache.has(avatarId)) {
      return avatarCache.get(avatarId);
    }
    const modelData = await this.fetchAndParseModel(sourceUrl, format);
    avatarCache.set(avatarId, modelData);
    return modelData;
  }

  static async fetchAndParseModel(url, format) {
    try {
      console.log('[AvatarLoader] Fetching avatar model from URL:', url);
      const response = await fetch(url);
      console.log('[AvatarLoader] Fetch response status:', response.status, response.statusText);
      if (!response.ok) {
        throw new Error('Failed to fetch avatar model. Status: ' + response.status + ' ' + response.statusText + '. URL: ' + url);
      }
      const rawData = await response.arrayBuffer();
      console.log('[AvatarLoader] Fetched avatar model, size (bytes):', rawData.byteLength);
      console.log('[AvatarLoader] Parsing format:', format);

      switch (format) {
        case 'GLTF':
          return this.parseGLTF(rawData);
        case 'FBX':
          return this.parseFBX(rawData);
        case 'OBJ':
          return this.parseOBJ(rawData);
        default:
          if (supportedParsers[format]) {
            return supportedParsers[format](rawData);
          }
          throw new Error('Unsupported avatar format: ' + format);
      }
    } catch (error) {
      console.error('[AvatarLoader] Error fetching/parsing avatar model from URL:', url, error);
      throw error;
    }
  }

  static parseGLTF(rawData) {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      loader.parse(rawData, '', (gltf) => {
        resolve(gltf);
      }, (error) => {
        reject(error);
      });
    });
  }

  static parseFBX(rawData) {
    // Placeholder: integrate with FBXLoader
    return rawData;
  }

  static parseOBJ(rawData) {
    // Placeholder: integrate with OBJLoader
    return rawData;
  }
}

///////////////////////
// AvatarCustomizer
///////////////////////

class AvatarCustomizer {
  static applySkin(avatarModel, skinData) {
    avatarModel.materials = this.generateMaterialsFromSkin(skinData);
  }

  static addAccessory(avatarModel, accessoryAsset) {
    this.attachToBone(avatarModel, accessoryAsset, accessoryAsset.targetBone);
  }

  static applyMorphTargets(avatarModel, morphData) {
    for (const [morphName, value] of Object.entries(morphData)) {
      avatarModel.setMorphValue(morphName, value);
    }
  }

  static resetCustomizations(avatarModel) {
    this.revertToBaseAppearance(avatarModel);
  }

  static generateMaterialsFromSkin(skinData) {
    // Placeholder: generate Three.js materials from skinData
    return skinData;
  }

  static attachToBone(avatarModel, accessoryAsset, targetBone) {
    // Placeholder: attach accessory to bone
  }

  static revertToBaseAppearance(avatarModel) {
    // Placeholder: reset avatar appearance
  }
}

///////////////////////
// AnimationController
///////////////////////

class AnimationController {
  static initializeAnimations(avatarModel, animationAssets) {
    this.loadAnimationsIntoModel(avatarModel, animationAssets);
    this.setDefaultState('idle');
  }

  static loadAnimationsIntoModel(avatarModel, animationAssets) {
    // Placeholder: load animations into avatar model
  }

  static setDefaultState(stateName) {
    this.currentState = stateName;
    this.playAnimation(stateName);
  }

  static playAnimation(stateName) {
    this.blendToAnimation(stateName);
  }

  static blendToAnimation(stateName) {
    // Placeholder: blend to target animation
  }

  static syncWithEvent(event) {
    switch (event.type) {
      case 'project_highlight':
        this.playAnimation('showcase');
        break;
      case 'skill_unlocked':
        this.playAnimation('celebrate');
        break;
      case 'idle':
        this.playAnimation('idle');
        break;
      default:
        this.playAnimation('idle');
    }
  }
}

///////////////////////
// RenderEngine
///////////////////////

class RenderEngine {
  static initRenderer(containerElement, config) {
    this.renderer = this.createRenderer(containerElement, config);
    this.scene = this.createScene();
    this.camera = this.createCamera(config.cameraSettings);
    this.addLights(this.scene, config.lighting);
    this.rendering = true;
    this.startRenderLoop();
    return this.renderer;
  }

  static createRenderer(containerElement, config) {
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(containerElement.clientWidth, containerElement.clientHeight);
    containerElement.appendChild(renderer.domElement);
    return renderer;
  }

  static createScene() {
    return new THREE.Scene();
  }

  static createCamera(cameraSettings) {
    const camera = new THREE.PerspectiveCamera(
      cameraSettings?.fov || 45,
      cameraSettings?.aspect || window.innerWidth / window.innerHeight,
      cameraSettings?.near || 0.1,
      cameraSettings?.far || 1000
    );
    camera.position.set(0, 1.6, 3);
    return camera;
  }

  static addLights(scene, lightingConfig) {
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.5);
    directionalLight.position.set(0, 10, 10);
    scene.add(directionalLight);

    // Add grid helper
    const gridHelper = new THREE.GridHelper(10, 10);
    scene.add(gridHelper);

    // Add axes helper
    const axesHelper = new THREE.AxesHelper(5);
    scene.add(axesHelper);
  }

  static addAvatarToScene(avatarModel) {
    if (this.scene && avatarModel) {
      if (avatarModel.scene) {
        // Force scale and position for debugging
        avatarModel.scene.scale.set(1, 1, 1);
        avatarModel.scene.position.set(0, 0, 0);
        avatarModel.scene.rotation.set(0, 0, 0);

        this.scene.add(avatarModel.scene);
        this.avatarScene = avatarModel.scene;

        // Add bounding box helper
        const bbox = new THREE.Box3().setFromObject(avatarModel.scene);
        const bboxHelper = new THREE.Box3Helper(bbox, 0xff0000);
        this.scene.add(bboxHelper);

        if (avatarModel.animations && avatarModel.animations.length > 0) {
          this.mixer = new THREE.AnimationMixer(avatarModel.scene);
          avatarModel.animations.forEach((clip) => {
            this.mixer.clipAction(clip).play();
          });
        }
      }
    }
  }

  static update() {
    this.updateAnimations();
    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  static updateAnimations() {
    if (this.mixer) {
      const delta = this.clock.getDelta();
      this.mixer.update(delta);
    }
  }

  static startRenderLoop() {
    if (!this.clock) {
      this.clock = new THREE.Clock();
    }
    const animate = () => {
      if (!this.rendering) return;
      this.update();
      requestAnimationFrame(animate);
    };
    animate();
  }
}

///////////////////////
// EventSyncAdapter
///////////////////////

class EventSyncAdapter {
  static subscribeToEvents(eventBus, avatarModel) {
    this.avatarModel = avatarModel;
    eventBus.on('profileEvent', (event) => this.handleProfileEvent(event));
    return { subscribed: true };
  }

  static handleProfileEvent(event) {
    AnimationController.syncWithEvent(event);
    if (event.type === 'customization_change') {
      AvatarCustomizer.applySkin(this.avatarModel, event.skinData);
    }
    return { handled: true };
  }
}

///////////////////////
// ExtensibilityHooks
///////////////////////

class ExtensibilityHooks {
  static registerAssetType(assetType, parserFunction) {
    supportedParsers[assetType] = parserFunction;
  }

  static registerAnimationPack(name, animationData) {
    animationLibrary[name] = animationData;
  }

  static overrideRenderer(customRenderer) {
    RenderEngine.renderer = customRenderer;
  }
  static invoke(hookName, payload) {
    // Placeholder: invoke registered hook
    return { hookName, payload };
  }
}

///////////////////////
// Exported Module API
///////////////////////

export {
  AvatarLoader,
  AvatarCustomizer,
  AnimationController,
  RenderEngine,
  EventSyncAdapter,
  ExtensibilityHooks,
};